import React, { useContext } from "react";

import {
  Box,
  Card,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles, withStyles } from "tss-react/mui";

import { colors } from "@/theme/overrides/colors";
import { percent, round } from "@/utils/number";

import MealPlanFormStore from "../store/MealPlanFormStore";

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    zIndex: 1,
    padding: theme.spacing(6, 3),
  },
  bold: {
    fontWeight: "bold",
  },
  hiddenLink: {
    display: "none",
  },
  textSecondary: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(0.5),
  },
  error: {
    color: theme.palette.error.main,
  },
  cal: {
    color: theme.palette.primary.main,
    fontWeight: "bolder",
  },
}));

const StyledTableHead = withStyles(TableHead, {
  root: {
    backgroundColor: colors.light,
  },
});

const StyledTableRow = withStyles(TableRow, {
  root: {
    backgroundColor: colors.white,
  },
});

const StyledTableCell = withStyles(TableCell, (theme: Theme) => ({
  root: {
    border: 0,
    padding: theme.spacing(4, 2),
    textAlign: "left",
  },
}));

const CalculationTable: React.FC = observer(() => {
  const { classes, cx } = useStyles();
  const store = useContext(MealPlanFormStore.Context);
  const recommendedCalories = store.recommendedCaloriesKcal;
  const macronutrients = store.macronutrients;
  const {
    totalCalories,
    totalCarbohydrateCalories,
    totalCarbohydrateGrams,
    totalCarbohydratePercent,
    totalFatCalories,
    totalFatGrams,
    totalFatPercent,
    totalProteinCalories,
    totalProteinGrams,
    totalProteinPercent,
  } = store.foodExchange.calculations;
  const exceeded = totalCalories > recommendedCalories;

  return (
    <>
      <TableContainer component={Card}>
        <Table aria-label="simple table">
          <StyledTableHead>
            <TableRow>
              <StyledTableCell></StyledTableCell>
              <StyledTableCell>Grams</StyledTableCell>
              <StyledTableCell>Calories</StyledTableCell>
              <StyledTableCell>Percent</StyledTableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            <StyledTableRow>
              <StyledTableCell className={classes.bold}>Carbs</StyledTableCell>
              <StyledTableCell>
                {round(totalCarbohydrateGrams, 2)}g
                <span className={classes.textSecondary}>
                  / {round(macronutrients.carbohydrateGrams, 2)}g
                </span>
              </StyledTableCell>
              <StyledTableCell>
                {round(totalCarbohydrateCalories, 2)}
                kcal
                <span className={classes.textSecondary}>
                  / {round(macronutrients.carbohydrateCalories)}kcal
                </span>
              </StyledTableCell>
              <StyledTableCell>
                {percent(totalCarbohydratePercent, { round: 2 })}% /{" "}
                {macronutrients.carbohydrates}%
              </StyledTableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell className={classes.bold}>
                Proteins
              </StyledTableCell>
              <StyledTableCell>
                {round(totalProteinGrams, 2)}g
                <span className={classes.textSecondary}>
                  / {round(macronutrients.proteinGrams, 2)}g
                </span>
              </StyledTableCell>
              <StyledTableCell>
                {round(totalProteinCalories, 2)}kcal
                <span className={classes.textSecondary}>
                  / {round(macronutrients.proteinCalories)}kcal
                </span>
              </StyledTableCell>
              <StyledTableCell>
                {percent(totalProteinPercent, { round: 2 })}% /{" "}
                {macronutrients.proteins}%
              </StyledTableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell className={classes.bold}>Fats</StyledTableCell>
              <StyledTableCell>
                {round(totalFatGrams, 2)}g
                <span className={classes.textSecondary}>
                  / {round(macronutrients.fatGrams, 2)}g
                </span>
              </StyledTableCell>
              <StyledTableCell>
                {round(totalFatCalories, 2)}kcal
                <span className={classes.textSecondary}>
                  / {round(macronutrients.fatCalories)}kcal
                </span>
              </StyledTableCell>
              <StyledTableCell>
                {percent(totalFatPercent, { round: 2 })}% /{" "}
                {macronutrients.fats}%
              </StyledTableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box marginTop={12}>
        <Typography className={classes.bold}>Calories</Typography>
        <Typography variant="h5" color="textSecondary">
          <span
            className={cx({
              [classes.error]: exceeded,
              [classes.cal]: !exceeded,
            })}
          >
            {round(totalCalories, 2)}
          </span>{" "}
          / {recommendedCalories}
        </Typography>
        {exceeded && (
          <FormHelperText error>Total calories exceeded</FormHelperText>
        )}
      </Box>
    </>
  );
});

export default CalculationTable;
